import { httpClient } from "@/shared/http/httpClient";
import { OperationError } from "@/shared/errors/auth-errors";
import type {
    DailyDaySummaryDto,
    DailyEnvelopeDto,
    DailyEventsDto,
    DailyExecutiveSeriesDto,
    DailyExecutiveSummaryDto,
    DailyFunnelDto,
    DailyIncidentCommandDto,
    DailyIncidentCommandResultDto,
    DailyIncidentsDto,
    DailyPlatformStatusDto,
    DailyStatusTodayDto,
    DailyQueuePageDto,
    MetricQuality,
} from "@/features/daily/types/daily.types";

const DAILY_BASE = "/api/v1/analytics/daily";

export const DAILY_API_PATHS = {
    status: `${DAILY_BASE}/status`,
    statusToday: `${DAILY_BASE}/status/today`,
    executiveSeries: `${DAILY_BASE}/executive/series`,
    executiveSummary: `${DAILY_BASE}/executive/summary`,
    queue: `${DAILY_BASE}/diagnostic/queue`,
    daySummary: `${DAILY_BASE}/diagnostic/day-summary`,
    funnel: `${DAILY_BASE}/funnel`,
    incidents: `${DAILY_BASE}/incidents`,
    export: `${DAILY_BASE}/export`,
    events: `${DAILY_BASE}/events`,
} as const;

// Enmienda C14: the operator's free-text search matches account emails, so it
// travels in a request header — never in a URL that can be shared, bookmarked or
// logged by a proxy.
export const DAILY_SEARCH_HEADER = "X-Daily-Search";
export const DAILY_TRUNCATED_HEADER = "X-Daily-Truncated";

// Enmienda C8: an expired cursor is a 400 whose public message is this exact
// token, so the caller can rewind to page 1 instead of showing a dead end.
export const DAILY_CURSOR_EXPIRED_MESSAGE = "cursor_expired";

// Ledger L1929: a response is never CAST into a view model. A field the backend
// omitted must surface as a contract error, not as a crash inside a component.
export class DailyContractError extends OperationError {}

/** D106: the widest page the day queue accepts, and how many pages one read may follow. */
export const DAILY_DAY_PAGE_SIZE = 1000;
export const DAILY_DAY_MAX_PAGES = 20;

export type DailyQueryParams = Readonly<Record<string, string>>;

export type DailyRequestOptions = {
    signal?: AbortSignal;
    search?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isNumberOrNull = (value: unknown): boolean => value === null || typeof value === "number";

// A Record keyed by the closed vocabulary: tsc rejects this object if the backend
// ever adds or removes a quality, which is the compile-time parity check that
// replaces a duplicated enum (C13).
const METRIC_QUALITY_KEYS: Record<MetricQuality, true> = {
    ok: true,
    partial: true,
    stale: true,
    insufficient: true,
    unsupported: true,
    error: true,
};

const isMetricQuality = (value: unknown): value is MetricQuality =>
    typeof value === "string" && Object.prototype.hasOwnProperty.call(METRIC_QUALITY_KEYS, value);

export const isDailyEnvelope = (value: unknown): value is DailyEnvelopeDto =>
    isRecord(value)
    && isMetricQuality(value.quality)
    && typeof value.evaluatedAt === "number"
    && isNumberOrNull(value.sourceAsOf)
    && isRecord(value.scope)
    && isRecord(value.window)
    && isRecord(value.coverage)
    && Array.isArray(value.reasonCodes);

const isMetric = (value: unknown): boolean =>
    isRecord(value)
    && isMetricQuality(value.quality)
    && isNumberOrNull(value.value)
    && typeof value.sample === "number";

const isPlatformStatus = (value: unknown): value is DailyPlatformStatusDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && typeof value.state === "string"
    && Array.isArray(value.capabilities)
    && Array.isArray(value.incidents)
    && isMetric(value.backlogOpen)
    && isMetric(value.backlogOverdue)
    && isMetric(value.throughputCompleted)
    && Array.isArray(value.impactByCurrency)
    && isNumberOrNull(value.oldestOpenAgeMs);

const isStatusToday = (value: unknown): value is DailyStatusTodayDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && typeof value.date === "string"
    && isRecord(value.hourlyToday)
    && Array.isArray(value.hourlyToday.hours)
    && isRecord(value.todayFunnel)
    && isRecord(value.todayFunnel.order)
    && isMetric(value.todayFunnel.order.conversion)
    && isRecord(value.todayFunnel.deposit)
    && isMetric(value.todayFunnel.deposit.conversion)
    && Array.isArray(value.priorityOperations);

const isQueuePage = (value: unknown): value is DailyQueuePageDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && typeof value.queue === "string"
    && Array.isArray(value.rows)
    // L350 truncation honesty: `truncated` is only meaningful next to a known
    // total, so both fields are load-bearing and both are validated.
    && isNumberOrNull(value.totalItems)
    && typeof value.truncated === "boolean"
    && (value.nextCursor === null || typeof value.nextCursor === "string")
    && Array.isArray(value.impactByCurrency);

const isFunnel = (value: unknown): value is DailyFunnelDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && Array.isArray(value.conversionJourney)
    && Array.isArray(value.executionJourney)
    && Array.isArray(value.stages)
    && isNumberOrNull(value.fundingWaitP95Ms);

const isDayPoint = (value: unknown): boolean =>
    isRecord(value)
    && typeof value.date === "string"
    && typeof value.dayOfMonth === "number"
    && typeof value.volume === "number"
    && typeof value.revenue === "number"
    && typeof value.completed === "number";

const isSeriesTotal = (value: unknown): boolean =>
    isRecord(value)
    && typeof value.volume === "number"
    && typeof value.revenue === "number"
    && typeof value.completed === "number";

const isExecutiveSeries = (value: unknown): value is DailyExecutiveSeriesDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && isRecord(value.referenceWindow)
    && Array.isArray(value.byCurrency)
    && value.byCurrency.every((entry) =>
        isRecord(entry)
        && (entry.currency === null || typeof entry.currency === "string")
        && Array.isArray(entry.current) && entry.current.every(isDayPoint)
        && Array.isArray(entry.reference) && entry.reference.every(isDayPoint)
        && isSeriesTotal(entry.currentTotal)
        && isSeriesTotal(entry.referenceTotal))
    && Array.isArray(value.bestDays)
    && value.bestDays.every((entry) =>
        isRecord(entry) && Array.isArray(entry.days) && entry.days.every(isDayPoint));

const isDaySummary = (value: unknown): value is DailyDaySummaryDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && typeof value.date === "string"
    && isRecord(value.volume) && Array.isArray(value.volume.byCurrency)
    && isRecord(value.revenue) && Array.isArray(value.revenue.byCurrency)
    && isRecord(value.successRate) && isMetric(value.successRate.metric)
    && isRecord(value.slowOrders) && isMetric(value.slowOrders.count) && isMetric(value.slowOrders.rate) && isMetric(value.slowOrders.slaHealth)
    && isRecord(value.endToEnd) && isMetric(value.endToEnd.p50) && isMetric(value.endToEnd.p95)
    && isRecord(value.fundingWait) && isMetric(value.fundingWait.p50) && isMetric(value.fundingWait.p95)
    && isRecord(value.feeDeviation) && isMetric(value.feeDeviation.count) && isMetric(value.feeDeviation.rate)
    && isRecord(value.creationGap) && isMetric(value.creationGap.gap);

const isExecutivePeriod = (value: unknown): boolean =>
    isRecord(value)
    && Array.isArray(value.completedVolume)
    && Array.isArray(value.revenue)
    && isMetric(value.completedOperations)
    && Array.isArray(value.takeRateByCurrency)
    && isMetric(value.successRate)
    && isMetric(value.p95Completion)
    && isMetric(value.slowRate)
    && Array.isArray(value.concentration);

const isExecutiveSummary = (value: unknown): value is DailyExecutiveSummaryDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && isRecord(value.targets)
    && typeof value.targets.p95CompletionMs === "number"
    && isExecutivePeriod(value.current)
    && isExecutivePeriod(value.previous)
    && Array.isArray(value.growth)
    && Array.isArray(value.risks)
    && Array.isArray(value.actions);

const isEvents = (value: unknown): value is DailyEventsDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && Array.isArray(value.events)
    && value.events.every((event) =>
        isRecord(event)
        && typeof event.at === "number"
        && typeof event.kind === "string"
        && typeof event.operationId === "string"
        && typeof event.operationType === "string");

const isIncidents = (value: unknown): value is DailyIncidentsDto =>
    isRecord(value)
    && isDailyEnvelope(value.envelope)
    && Array.isArray(value.incidents)
    && isNumberOrNull(value.totalItems)
    && (value.nextCursor === null || typeof value.nextCursor === "string");

const isCommandResult = (value: unknown): value is DailyIncidentCommandResultDto =>
    isRecord(value)
    && typeof value.applied === "boolean"
    && typeof value.alreadyApplied === "boolean"
    && isRecord(value.incident)
    && typeof value.incident.incidentId === "string"
    && typeof value.incident.episode === "number";

const withQuery = (path: string, params: DailyQueryParams): string => {
    const search = new URLSearchParams();
    Object.keys(params).sort().forEach((key) => {
        const value = params[key];
        if (value !== "") search.append(key, value);
    });
    const serialized = search.toString();
    return serialized === "" ? path : `${path}?${serialized}`;
};

const searchHeaders = (options?: DailyRequestOptions): HeadersInit | undefined => {
    const search = options?.search;
    if (search === undefined || search === "") return undefined;
    return { [DAILY_SEARCH_HEADER]: search };
};

const fetchDto = async <T>(
    path: string,
    params: DailyQueryParams,
    guard: (value: unknown) => value is T,
    resource: string,
    options?: DailyRequestOptions,
): Promise<T> => {
    const body = await httpClient.getJson<unknown>(withQuery(path, params), searchHeaders(options), options?.signal);
    if (!guard(body)) {
        throw new DailyContractError(`The Daily ${resource} response is missing fields the contract requires`);
    }
    return body;
};

const sendCommand = async (
    segment: string,
    command: DailyIncidentCommandDto,
    signal?: AbortSignal,
): Promise<DailyIncidentCommandResultDto> => {
    const path = `${DAILY_API_PATHS.incidents}/${encodeURIComponent(command.incidentId)}/${segment}`;
    const body = await httpClient.postJson<unknown>(path, command, undefined, signal);
    if (!isCommandResult(body)) {
        throw new DailyContractError("The Daily incident command response is missing fields the contract requires");
    }
    return body;
};

export const dailyApi = {
    getStatus: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyPlatformStatusDto> =>
        fetchDto(DAILY_API_PATHS.status, params, isPlatformStatus, "status", options),

    /** B16 (D134): today hour by hour, today's funnel and the five operations to look at first. */
    getStatusToday: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyStatusTodayDto> =>
        fetchDto(DAILY_API_PATHS.statusToday, params, isStatusToday, "status today", options),

    getExecutiveSeries: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyExecutiveSeriesDto> =>
        fetchDto(DAILY_API_PATHS.executiveSeries, params, isExecutiveSeries, "executive series", options),

    getExecutiveSummary: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyExecutiveSummaryDto> =>
        fetchDto(DAILY_API_PATHS.executiveSummary, params, isExecutiveSummary, "executive summary", options),

    getQueue: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyQueuePageDto> =>
        fetchDto(DAILY_API_PATHS.queue, params, isQueuePage, "queue", options),

    /**
     * D106 «pinta todas las del día»: follows the signed cursor until the server says
     * there is nothing left, and answers ONE page holding every row. A read that would
     * need more than DAILY_DAY_MAX_PAGES pages stops there and says so through
     * `truncated`, instead of looping without bound or pretending to be complete.
     */
    getWholeQueue: async (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyQueuePageDto> => {
        const first = await dailyApi.getQueue({ ...params, size: String(DAILY_DAY_PAGE_SIZE), cursor: "" }, options);
        const rows = [...first.rows];
        let cursor = first.nextCursor;
        let pages = 1;
        let truncated = first.truncated;
        while (cursor !== null) {
            if (pages >= DAILY_DAY_MAX_PAGES) {
                truncated = true;
                break;
            }
            const next = await dailyApi.getQueue({ ...params, size: String(DAILY_DAY_PAGE_SIZE), cursor }, options);
            rows.push(...next.rows);
            truncated = truncated || next.truncated;
            cursor = next.nextCursor;
            pages += 1;
        }
        return { ...first, rows, nextCursor: null, truncated };
    },

    getDaySummary: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyDaySummaryDto> =>
        fetchDto(DAILY_API_PATHS.daySummary, params, isDaySummary, "day summary", options),

    getFunnel: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyFunnelDto> =>
        fetchDto(DAILY_API_PATHS.funnel, params, isFunnel, "funnel", options),

    getEvents: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyEventsDto> =>
        fetchDto(DAILY_API_PATHS.events, params, isEvents, "events", options),

    listIncidents: (params: DailyQueryParams, options?: DailyRequestOptions): Promise<DailyIncidentsDto> =>
        fetchDto(DAILY_API_PATHS.incidents, params, isIncidents, "incidents", options),

    // C14: the export must read its truncation flag off the response, so it goes
    // through `request` rather than a JSON helper.
    exportQueue: async (
        params: DailyQueryParams,
        options?: DailyRequestOptions,
    ): Promise<{ blob: Blob; truncated: boolean }> => {
        const response = await httpClient.request(withQuery(DAILY_API_PATHS.export, params), {
            method: "GET",
            headers: searchHeaders(options),
            signal: options?.signal,
        });
        return { blob: await response.blob(), truncated: response.headers.get(DAILY_TRUNCATED_HEADER) === "true" };
    },

    // C4: the caller owns `idempotencyKey` and `expectedEpisode`, so a retry of the
    // same intent stays the same command and can never land on a later episode.
    acknowledge: (command: DailyIncidentCommandDto, signal?: AbortSignal): Promise<DailyIncidentCommandResultDto> =>
        sendCommand("ack", command, signal),

    assign: (command: DailyIncidentCommandDto, signal?: AbortSignal): Promise<DailyIncidentCommandResultDto> =>
        sendCommand("assign", command, signal),

    unassign: (command: DailyIncidentCommandDto, signal?: AbortSignal): Promise<DailyIncidentCommandResultDto> =>
        sendCommand("unassign", command, signal),
};
